/**
 * usePermissionsService
 *
 * Talks to the standalone WIP Permissions Service (see the separate
 * wip-permissions-service project) -- a tiny Node/Express service with
 * its own JSON-file datastore, completely separate from SSS/TRS. Not
 * built on apiClient.js's createApiClient() factory since this service
 * doesn't use the SSS JWT or X-Branch-ID header at all -- it's a
 * different kind of backend with a different (much simpler) contract.
 *
 * Design choice: ProtectedRoute.jsx FAILS OPEN if this service is
 * unreachable (treats it as "allowed") rather than locking everyone out
 * of the whole app because a small side-service happens to be down --
 * see useCurrentUserAccess() below. The User Permissions page itself
 * shows an honest error state instead when the service is unreachable,
 * rather than silently falling back to some other storage that
 * wouldn't actually sync across devices (which would quietly reintroduce
 * the exact problem this service exists to fix).
 *
 * If the service has API_KEY set (see its own README), set the SAME
 * value here as VITE_PERMISSIONS_API_KEY -- sent as `x-api-key` on every
 * request. Left unset, no key is sent, matching the service's own
 * unprotected-by-default local-dev mode.
 *
 * Admin-only writes: useSetUserAccessMutation() sends the acting user's
 * role as `x-user-role`, which the service requires to equal "admin" or
 * it 403s. Same trust-the-header caveat as x-user-id -- real server-side
 * rejection of non-admins, but not cryptographic proof of role.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const permissionsApiClient = axios.create({
  baseURL: import.meta.env.VITE_PERMISSIONS_API_BASE_URL ?? 'http://localhost:5100',
  headers: {
    Accept: 'application/json',
    ...(import.meta.env.VITE_PERMISSIONS_API_KEY
      ? { 'x-api-key': import.meta.env.VITE_PERMISSIONS_API_KEY }
      : {}),
  },
});

/** @typedef {'allowed'|'restricted'} AccessState */

/** Full permissions map -- used by the User Permissions table. */
export const usePermissionsMap = () =>
  useQuery({
    queryKey: ['permissions', 'all'],
    queryFn: async ({ signal }) => {
      const { data } = await permissionsApiClient.get('/api/permissions', { signal });
      return /** @type {Record<string, { access: AccessState }>} */ (data);
    },
    staleTime: 10_000,
    retry: 1,
  });

/**
 * One user's access. Used by ProtectedRoute (wrapped by
 * useCurrentUserAccess below) and anywhere else that needs a single
 * user's status without the full map.
 * @param {string|null} userId
 */
export const useUserAccessQuery = (userId) =>
  useQuery({
    queryKey: ['permissions', 'user', userId],
    queryFn: async ({ signal }) => {
      const { data } = await permissionsApiClient.get(`/api/permissions/${userId}`, { signal });
      return /** @type {AccessState} */ (data.access);
    },
    enabled: Boolean(userId),
    staleTime: 10_000,
    retry: 1,
  });

/**
 * Fail-open wrapper for ProtectedRoute: while loading, treat as
 * "allowed" (don't block on a slow network round-trip); if the service
 * errors (unreachable, down, etc.), ALSO treat as "allowed" -- a
 * side-service outage should never lock everyone out of the whole app.
 * Only an explicit "restricted" response actually blocks.
 * @param {string|null} userId
 */
export function useCurrentUserAccess(userId) {
  const query = useUserAccessQuery(userId);
  const access = query.isSuccess ? query.data : 'allowed';
  return { access, isChecking: query.isLoading, isServiceUnreachable: query.isError };
}

/** Sets one user's access; invalidates the permissions queries on success. Requires the acting user to have the admin role (server-enforced via x-user-role). */
export function useSetUserAccessMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    /** @param {{ userId: string, access: AccessState, actingUserId?: string|number, actingUserRole?: string }} vars */
    mutationFn: async ({ userId, access, actingUserId, actingUserRole }) => {
      const headers = {};
      if (actingUserId) headers['x-user-id'] = String(actingUserId);
      if (actingUserRole) headers['x-user-role'] = actingUserRole;

      const { data } = await permissionsApiClient.put(`/api/permissions/${userId}`, { access }, { headers });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
    },
  });
}
